import React, { useMemo } from 'react';

import { SynchronizeArrowsWarning } from 'src/app/components/icons/synchronize-arrows-warning';
import { LD } from 'src/app/constants/launch-darkly-flags';
import { useLDFlag } from 'src/app/hooks/use-ld-flag';

import { firstTimeCustomerOptions } from '../../../models';
import { AvailableToWithOverflow } from '../../shared/available-to-with-overflow';
import { getDayOfTheWeekText, getEndDateText, getStartDateText, getTimeRangeText } from '../../shared/helpers';
import { useSelectedDetailsResources } from '../../shared/use-selected-details-resources';
import { useSyncDisabledStatus } from '../../shared/use-sync-disabled-status';
import { useDiscountWizardStateContext } from '../../state';

import { SummaryCard } from './summary-card';

import type { SummaryCardProps } from './summary-card';

export function DetailsCard({ onEditClick }: { onEditClick: () => void }) {
  const isOrderSourcesEnabled = useLDFlag(LD.DISCOUNT_BY_ORDER_TYPE_MENU_TYPE, false);
  const {
    detailsStepState: {
      activeDaysOfTheWeek,
      availableOnline,
      endDate,
      endTime,
      firstTimeCustomerOption,
      limitPerCustomer,
      limitPerCustomerQuantity,
      restrictByDayOfWeek,
      specifyEndDate,
      startDate,
      startTime,
    },
  } = useDiscountWizardStateContext();
  const {
    selectedCustomerTypes,
    selectedCustomerGroups,
    selectedLocations,
    selectedCustomerSegments,
    selectedOrderSources,
  } = useSelectedDetailsResources();

  const {
    customerLimitDisabledSync,
    selectedCustomerGroupsDisabledSync,
    firstTimeCustomerDisabledSync,
    selectedCustomerSegmentsDisabledSync,
  } = useSyncDisabledStatus();

  const rows = useMemo(() => {
    const rows: SummaryCardProps['rows'] = [
      {
        fieldTitle: 'Customer type(s):',
        fieldValue: <AvailableToWithOverflow noun='customer types' options={selectedCustomerTypes} />,
      },
      {
        fieldTitle: <>Customer group(s):{selectedCustomerGroupsDisabledSync && <YellowSynchronizeArrowsWarning />}</>,
        fieldValue: <AvailableToWithOverflow noun='customer groups' options={selectedCustomerGroups} />,
        keyTitle: 'customer groups',
      },
      {
        fieldTitle: (
          <>Customer segment(s):{selectedCustomerSegmentsDisabledSync && <YellowSynchronizeArrowsWarning />}</>
        ),
        fieldValue: <AvailableToWithOverflow noun='customer segments' options={selectedCustomerSegments} />,
        keyTitle: 'customer segments',
      },
      {
        fieldTitle: <>Customer limit:{customerLimitDisabledSync && <YellowSynchronizeArrowsWarning />}</>,
        fieldValue: limitPerCustomer ? limitPerCustomerQuantity : 'None',
        keyTitle: 'customer limit',
      },
      {
        fieldTitle: <>First time customer:{firstTimeCustomerDisabledSync && <YellowSynchronizeArrowsWarning />}</>,
        fieldValue: firstTimeCustomerOptions[firstTimeCustomerOption].name,
        keyTitle: 'first time customer',
      },
      {
        fieldTitle: 'Schedule',
        fieldValue: <>Starts: {getStartDateText(startDate)}</>,
      },
      { fieldTitle: '', fieldValue: <>Ends: {getEndDateText(endDate, specifyEndDate)}</> },
      restrictByDayOfWeek
        ? { fieldTitle: 'Recurrence', fieldValue: <>{getDayOfTheWeekText(activeDaysOfTheWeek)}</> }
        : undefined,
      restrictByDayOfWeek ? { fieldTitle: '', fieldValue: <>{getTimeRangeText(startTime, endTime)}</> } : undefined,
      {
        fieldTitle: 'Location(s) available:',
        fieldValue: <AvailableToWithOverflow noun='locations' options={selectedLocations} />,
      },
      {
        fieldTitle: 'Available online:',
        fieldValue: availableOnline ? 'Yes' : 'No',
      },
      isOrderSourcesEnabled && availableOnline
        ? {
            fieldTitle: 'Order source(s):',
            fieldValue: <AvailableToWithOverflow noun='order sources' options={selectedOrderSources} />,
          }
        : undefined,
    ];

    return rows;
  }, [
    activeDaysOfTheWeek,
    availableOnline,
    isOrderSourcesEnabled,
    customerLimitDisabledSync,
    endDate,
    endTime,
    firstTimeCustomerDisabledSync,
    firstTimeCustomerOption,
    limitPerCustomer,
    limitPerCustomerQuantity,
    restrictByDayOfWeek,
    selectedCustomerGroups,
    selectedCustomerGroupsDisabledSync,
    selectedCustomerSegments,
    selectedCustomerSegmentsDisabledSync,
    selectedCustomerTypes,
    selectedLocations,
    selectedOrderSources,
    specifyEndDate,
    startDate,
    startTime,
  ]);

  return <SummaryCard rows={rows} title='Details' onEditClick={onEditClick} />;
}

function YellowSynchronizeArrowsWarning() {
  return <SynchronizeArrowsWarning color='var(--color-yellow-100)' />;
}
